import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaFileInvoice,
  FaDownload,
  FaSearch,
  FaFilter,
  FaEye,
  FaEnvelope,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaPrint
} from 'react-icons/fa';
import { invoiceService } from '../../../../services/invoiceService';
import { toast } from 'react-toastify';
import { formatPrice } from '../../../../utils/currency';
import { formatInvoiceDate, getInvoiceStatusBadge } from '../../../../utils/invoice';
import Invoice from '../../../Invoice';

const Invoices = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    cancelled: 0,
    totalAmount: 0
  });

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [statusFilter, dateRange]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const filters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        dateRange
      };
      const data = await invoiceService.getAllInvoices(filters);
      setInvoices(Array.isArray(data) ? data : data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await invoiceService.getInvoiceStats({ dateRange });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch invoice stats:', error);
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const matchesSearch = !searchTerm ||
        inv.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [invoices, searchTerm]);

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleSendEmail = async (invoiceNumber) => {
    try {
      await invoiceService.sendInvoiceEmail(invoiceNumber);
      toast.success('Invoice sent to email successfully!');
    } catch (error) {
      toast.error('Failed to send invoice: ' + error.message);
    }
  };

  const handleDownloadPDF = async (invoiceNumber) => {
    try {
      const blob = await invoiceService.downloadInvoicePDF(invoiceNumber);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download invoice: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Invoice Management</h2>
          <p className="text-xs text-gray-600 mt-1">Manage and track all invoices</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-1">Total Invoices</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total || 0}</p>
            </div>
            <FaFileInvoice className="text-2xl text-blue-600" />
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-green-700 mb-1">Paid</p>
              <p className="text-2xl font-bold text-green-900">{stats.paid || 0}</p>
            </div>
            <FaCheckCircle className="text-2xl text-green-600" />
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-yellow-700 mb-1">Pending</p>
              <p className="text-2xl font-bold text-yellow-900">{stats.pending || 0}</p>
            </div>
            <FaSpinner className="text-2xl text-yellow-600" />
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-6 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-red-700 mb-1">Cancelled</p>
              <p className="text-2xl font-bold text-red-900">{stats.cancelled || 0}</p>
            </div>
            <FaTimesCircle className="text-2xl text-red-600" />
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-purple-700 mb-1">Total Amount</p>
              <p className="text-2xl font-bold text-purple-900">{formatPrice(stats.totalAmount || 0)}</p>
            </div>
            <FaFileInvoice className="text-2xl text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="generated">Generated</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        {loading && invoices.length === 0 ? (
          <div className="p-20 text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Loading invoices...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => {
                    const statusBadge = getInvoiceStatusBadge(invoice.status);
                    return (
                      <tr key={invoice.id || invoice.invoiceNumber} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{invoice.orderId}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {invoice.customer?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatInvoiceDate(invoice.date || invoice.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {formatPrice(invoice.total || 0)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewInvoice(invoice)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Invoice"
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(invoice.invoiceNumber)}
                              className="text-green-600 hover:text-green-900"
                              title="Download PDF"
                            >
                              <FaDownload />
                            </button>
                            <button
                              onClick={() => handleSendEmail(invoice.invoiceNumber)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Send via Email"
                            >
                              <FaEnvelope />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      <AnimatePresence>
        {showInvoiceModal && selectedInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto"
            onClick={() => {
              setShowInvoiceModal(false);
              setSelectedInvoice(null);
            }}
          >
            <div className="min-h-screen p-4" onClick={(e) => e.stopPropagation()}>
              <div className="bg-white rounded-lg shadow-xl max-w-5xl mx-auto my-8">
                <Invoice
                  invoiceData={selectedInvoice}
                  onClose={() => {
                    setShowInvoiceModal(false);
                    setSelectedInvoice(null);
                  }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Invoices;


